import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AppRouter } from 'api/src/types'
import type { inferRouterOutputs } from '@trpc/server'
import { useSubscription } from '@trpc/tanstack-react-query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { BookOpen } from 'lucide-react'

import { LobbyMyGames } from '@/components/lobby-my-games'
import { InlineRegistration } from '@/components/inline-registration'
import { RoomSetup } from '@/components/room/RoomSetup'
import { RoomPaymentLabel } from '@/components/room/room-payment-label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import {
  roomStreamDeliversUpdates,
  useRoomStreamEligibility,
} from '@/lib/room-stream'
import {
  LOBBY_FILTER_LANGUAGES,
  LOBBY_RULES_PATH,
  lobbyEventAction,
  languageNameKey,
  lobbyJoinOutcome,
  lobbyJoinOutcomeKey,
  lobbyViewFromSearch,
  lobbyViewSearch,
  resolveLobbyCta,
  STATUS_CHIPS,
  toggleLobbyLanguage,
  type LobbyJoinOutcome,
  type LobbyView,
} from '@/lib/lobby'
import { returnTargetFrom, verifyEmailUrlReturningTo } from '@/lib/return-target'
import { useTRPC } from '@/lib/trpc'
import type { LanguageCode } from '@/lib/url-language-utils'
import { cn } from '@/lib/utils'

type LobbyRoom = inferRouterOutputs<AppRouter>['roomRouter']['publicLobby']['rooms'][number]

export function LobbyPage() {
  const trpc = useTRPC()
  const { t, language } = useI18n()
  const { user, actor, authResolved } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const { languages, statusFilter, cursors } = useMemo(
    () => lobbyViewFromSearch(location.search),
    [location.search],
  )
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const personalView = searchParams.get('view') === 'mine'
  const awaitingOnly = searchParams.get('mineFilter') === 'awaiting'
  const updatePersonalView = (key: 'view' | 'mineFilter', value: string) => {
    const next = new URLSearchParams(location.search)
    next.set(key, value)
    navigate({ pathname: location.pathname, search: `?${next}`, hash: location.hash })
  }
  const [outcomes, setOutcomes] = useState<Record<string, LobbyJoinOutcome>>({})

  const cursor = cursors.at(-1)
  const eligibleForStream = useRoomStreamEligibility(actor, authResolved)
  const [streamLive, setStreamLive] = useState(false)
  const streaming = eligibleForStream && streamLive

  const lobbyQuery = useQuery({
    ...trpc.roomRouter.publicLobby.queryOptions({
      gameLanguages: languages.length > 0 ? languages : undefined,
      statusFilter,
      cursor,
    }),
    enabled: !personalView,
    refetchInterval: streaming ? 30_000 : 5_000,
  })

  const membershipQuery = useQuery({
    ...trpc.roomRouter.myRoomCodes.queryOptions(),
    enabled: authResolved && Boolean(user),
    staleTime: 15_000,
  })
  const myRoomCodes = useMemo(
    () => new Set(authResolved && user ? membershipQuery.data?.roomCodes ?? [] : []),
    [authResolved, membershipQuery.data, user],
  )

  const [prepended, setPrepended] = useState<LobbyRoom[]>([])
  const [replaced, setReplaced] = useState<Record<string, LobbyRoom>>({})
  const [removed, setRemoved] = useState<string[]>([])
  const [newRoomCount, setNewRoomCount] = useState(0)

  const resetOptimistic = useCallback(() => {
    setPrepended([])
    setReplaced({})
    setRemoved([])
    setNewRoomCount(0)
  }, [])

  const updateView = useCallback((view: LobbyView) => {
    const search = lobbyViewSearch(location.search, view)
    if (search !== location.search) {
      navigate({
        pathname: location.pathname,
        search,
        hash: location.hash,
      })
    }
    resetOptimistic()
  }, [location.hash, location.pathname, location.search, navigate, resetOptimistic])

  useEffect(() => {
    resetOptimistic()
  }, [location.search, resetOptimistic])

  const joinMutation = useMutation(trpc.roomRouter.join.mutationOptions())

  const serverRooms = lobbyQuery.data?.rooms ?? []
  const rooms = [...prepended, ...serverRooms]
    .filter(room => !removed.includes(room.roomCode))
    .map(room => replaced[room.roomCode] ?? room)
  const nextCursor = lobbyQuery.data?.nextCursor ?? null
  const pageIndex = cursors.length

  const lobbySubscription = useSubscription(
    trpc.roomRouter.onLobbyChanged.subscriptionOptions(undefined, {
      enabled: eligibleForStream && !personalView,
      onData: (event) => {
        const action = lobbyEventAction(event, {
          pageIndex,
          languages,
          visibleRoomCodes: rooms.map(room => room.roomCode),
        })
        switch (action.kind) {
          case 'prepend': {
            if ('room' in event)
              setPrepended(current => [event.room, ...current])
            break
          }
          case 'replace': {
            if ('room' in event)
              setReplaced(current => ({ ...current, [event.room.roomCode]: event.room }))
            break
          }
          case 'remove': {
            if (event.type === 'room.delisted')
              setRemoved(current => [...current, event.roomCode])
            break
          }
          case 'resync': {
            resetOptimistic()
            void lobbyQuery.refetch()
            break
          }
          case 'notify': {
            setNewRoomCount(current => current + 1)
            break
          }
          default: {
            break
          }
        }
      },
      onError: () => {},
      onConnectionStateChange: (state) => {
        if (state.state === 'pending') {
          resetOptimistic()
          void lobbyQuery.refetch()
        }
      },
    }),
  )

  useEffect(() => {
    setStreamLive(
      roomStreamDeliversUpdates({
        eligible: eligibleForStream,
        status: lobbySubscription.status,
      }),
    )
  }, [eligibleForStream, lobbySubscription.status])

  const setFilter = useCallback((code: LanguageCode | null) => {
    updateView({
      languages: code === null ? [] : toggleLobbyLanguage(languages, code),
      statusFilter,
      cursors: [],
    })
  }, [languages, statusFilter, updateView])

  const [pendingRoomCode, setPendingRoomCode] = useState<string | null>(null)

  const joinRoom = useCallback(
    async (roomCode: string) => {
      if (user && !user.gameIdentityVerified) {
        navigate(verifyEmailUrlReturningTo(`/room/${encodeURIComponent(roomCode)}`))
        return
      }
      const target = rooms.find(room => room.roomCode === roomCode)
      if (!target || (!target.freeGame && target.paymentMode === 'split')) {
        navigate(`/room/${roomCode}`)
        return
      }
      setOutcomes(current => ({ ...current, [roomCode]: 'joined' }))
      try {
        await joinMutation.mutateAsync({ roomId: roomCode })
        navigate(`/room/${roomCode}`)
      }
      catch (error) {
        const isAlreadyMember
          = error instanceof TRPCClientError
            && (error.data?.code === 'CONFLICT' || error.message === 'already_member')
        if (isAlreadyMember) {
          navigate(`/room/${roomCode}`)
          return
        }
        const message
          = error instanceof TRPCClientError ? error.message : undefined
        setOutcomes(current => ({
          ...current,
          [roomCode]: lobbyJoinOutcome(message),
        }))
        void lobbyQuery.refetch()
      }
    },
    [joinMutation, lobbyQuery, navigate, rooms, user],
  )

  const handleJoin = useCallback(
    async (roomCode: string) => {
      if (!user) {
        setPendingRoomCode(roomCode)
        return
      }
      await joinRoom(roomCode)
    },
    [joinRoom, user],
  )

  const filterChips = useMemo(
    () =>
      LOBBY_FILTER_LANGUAGES.map(code => ({
        code,
        label: t(languageNameKey(code)),
        selected: languages.includes(code),
      })),
    [languages, t],
  )

  const allLanguagesSelected = languages.length === 0

  return (
    <div
      data-village-page="lobby"
      dir={language === 'ar' ? 'rtl' : undefined}
      className="mx-auto w-full max-w-3xl px-4 pb-24 pt-20"
    >
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('nav.lobby')}>
          <Button
            type="button"
            variant={!personalView ? 'default' : 'outline'}
            aria-pressed={!personalView}
            data-testid="lobby-view-world"
            onClick={() => updatePersonalView('view', 'world')}
          >
            {t('lobby.worldRooms')}
          </Button>
          <Button
            type="button"
            variant={personalView ? 'default' : 'outline'}
            aria-pressed={personalView}
            data-testid="lobby-view-mine"
            onClick={() => updatePersonalView('view', 'mine')}
          >
            {t('lobby.myGames')}
          </Button>
        </div>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to={LOBBY_RULES_PATH} data-testid="lobby-rules-button">
            <BookOpen className="size-4" aria-hidden="true" />
            {t('lobby.rules.button')}
          </Link>
        </Button>
      </header>

      {personalView ? (
        <section aria-label={t('lobby.myGames')}>
          <div className="mb-6 flex flex-wrap gap-2" data-testid="my-games-filter">
            <Button
              size="sm"
              variant={!awaitingOnly ? 'default' : 'outline'}
              aria-pressed={!awaitingOnly}
              data-testid="my-games-filter-all"
              onClick={() => updatePersonalView('mineFilter', 'all')}
            >
              {t('dashboard.games.filters.all')}
            </Button>
            <Button
              size="sm"
              variant={awaitingOnly ? 'default' : 'outline'}
              aria-pressed={awaitingOnly}
              data-testid="my-games-filter-awaiting"
              onClick={() => updatePersonalView('mineFilter', 'awaiting')}
            >
              {t('gameAttention.awaitingInput')}
            </Button>
          </div>
          <LobbyMyGames awaitingOnly={awaitingOnly} />
        </section>
      ) : (
        <>
          {newRoomCount > 0 && (
            <div className="mb-4">
              <Button
                variant="secondary"
                className="w-full"
                data-testid="lobby-new-rooms"
                onClick={() => {
                  updateView({ languages, statusFilter, cursors: [] })
                  void lobbyQuery.refetch()
                }}
              >
                {t('lobby.newRooms', { count: newRoomCount })}
              </Button>
            </div>
          )}

          <section className="mb-6" aria-label={t('lobby.filterLabel')}>
            <div className="flex flex-wrap gap-2" data-testid="lobby-language-filter">
              {
              }
              <Button
                size="sm"
                variant={allLanguagesSelected ? 'default' : 'outline'}
                aria-pressed={allLanguagesSelected}
                data-testid="lobby-language-all"
                onClick={() => {
                  if (!allLanguagesSelected)
                    setFilter(null)
                }}
              >
                {t('lobby.allLanguages')}
              </Button>
              {filterChips.map(chip => (
                <Button
                  key={chip.code}
                  size="sm"
                  variant={chip.selected ? 'default' : 'outline'}
                  aria-pressed={chip.selected}
                  data-testid={`lobby-language-${chip.code}`}
                  onClick={() => setFilter(chip.code)}
                >
                  {chip.label}
                </Button>
              ))}
            </div>

            {
            }
            <div className="mt-2 flex flex-wrap gap-2" data-testid="lobby-status-filter">
              {STATUS_CHIPS.map(chip => (
                <Button
                  key={chip.value}
                  size="sm"
                  variant={statusFilter === chip.value ? 'default' : 'outline'}
                  aria-pressed={statusFilter === chip.value}
                  data-testid={`lobby-status-${chip.value}`}
                  onClick={() => {
                    if (statusFilter === chip.value)
                      return
                    updateView({ languages, statusFilter: chip.value, cursors: [] })
                  }}
                >
                  {t(chip.labelKey)}
                </Button>
              ))}
            </div>
          </section>

          {lobbyQuery.isPending
            ? (
                <p className="text-sm text-muted-foreground">{t('lobby.loading')}</p>
              )
            : rooms.length === 0
              ? (
                  <p className="text-sm text-muted-foreground" data-testid="lobby-empty">
                    {t('lobby.empty')}
                  </p>
                )
              : (
                  <ul className="space-y-3" data-testid="lobby-room-list">
                    {rooms.map((room) => {
                      const outcome = outcomes[room.roomCode]
                      const settled = outcome && outcome !== 'joined'
                      const isMember = myRoomCodes.has(room.roomCode)
                      const cta = resolveLobbyCta({
                        cta: room.cta,
                        inProgress: room.inProgress,
                        settled: Boolean(settled),
                        isMember,
                      })
                      return (
                        <li key={room.roomCode} data-testid="lobby-room-row">
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="flex items-start justify-between gap-2 text-base">
                                {
                                }
                                <span className="min-w-0 space-y-0.5">
                                  {
                                  }
                                  {room.name && (
                                    <span
                                      className="block truncate font-medium text-foreground"
                                      data-testid="lobby-room-name"
                                    >
                                      {room.name}
                                    </span>
                                  )}
                                  <span className="block" data-testid="lobby-room-setting">
                                    <RoomSetup
                                      compact
                                      gameSetting={room.gameSetting}
                                      seatCount={room.capacity}
                                    />
                                  </span>
                                </span>
                                <span className="flex shrink-0 items-center gap-1">
                                  {room.inProgress && (
                                    <Badge data-testid="lobby-room-in-progress">
                                      {t('lobby.statusPlaying')}
                                    </Badge>
                                  )}
                                  <Badge variant="secondary" data-testid="lobby-room-language">
                                    {t(languageNameKey(room.language ?? 'en'))}
                                  </Badge>
                                </span>
                              </CardTitle>
                              {room.host && (
                                <p className="text-xs text-muted-foreground">
                                  {t('lobby.host')}
                                  {': '}
                                  <Link
                                    to={`/players/${encodeURIComponent(room.host.username)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline underline-offset-2 hover:text-foreground"
                                    data-testid="lobby-room-host"
                                  >
                                    {room.host.username}
                                  </Link>
                                </p>
                              )}
                              <RoomPaymentLabel
                                freeGame={room.freeGame}
                                paymentMode={room.paymentMode}
                              />
                            </CardHeader>
                            <CardContent className="flex items-center justify-between gap-3">
                              <span
                                className="text-sm text-muted-foreground"
                                data-testid="lobby-room-seats"
                              >
                                {room.capacity === null
                                  ? t('lobby.seatsUnknown', {
                                      humans: room.humanCount ?? 0,
                                    })
                                  : t('lobby.seats', {
                                      humans: room.humanCount ?? 0,
                                      capacity: room.capacity,
                                    })}
                              </span>
                              <div className="flex items-center gap-2">
                                {pendingRoomCode === room.roomCode && (
                                  <div className="w-full" />
                                )}
                                {outcome && (
                                  <span
                                    className={cn(
                                      'text-xs',
                                      outcome === 'joined'
                                        ? 'text-muted-foreground'
                                        : 'text-destructive',
                                    )}
                                    data-testid="lobby-room-outcome"
                                  >
                                    {t(lobbyJoinOutcomeKey(outcome))}
                                  </span>
                                )}
                                {cta !== null && (
                                  <Button
                                    size="sm"
                                    variant={cta === 'return' ? 'outline' : 'default'}
                                    disabled={(cta !== 'join' && cta !== 'return')
                                      || outcome === 'joined'}
                                    data-testid="lobby-join-button"
                                    data-room-code={room.roomCode}
                                    data-cta={cta}
                                    onClick={() => {
                                      if (cta === 'return') {
                                        navigate(`/room/${room.roomCode}`)
                                        return
                                      }
                                      handleJoin(room.roomCode).catch(() => {})
                                    }}
                                  >
                                    {
                                    }
                                    {cta === 'return'
                                      ? t('lobby.returnToRoom')
                                      : cta === 'join'
                                        ? t('lobby.joinAction')
                                        : cta === 'open'
                                          ? t('lobby.open')
                                          : t('lobby.unavailable')}
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                            {pendingRoomCode === room.roomCode && (
                              <CardContent className="border-t pt-4">
                                <InlineRegistration
                                  returnTarget={returnTargetFrom(window.location)}
                                  onCancel={() => setPendingRoomCode(null)}
                                  onRegistered={async () => {
                                    setPendingRoomCode(null)
                                    await joinRoom(room.roomCode)
                                  }}
                                />
                              </CardContent>
                            )}
                          </Card>
                        </li>
                      )
                    })}
                  </ul>
                )}

          {nextCursor && (
            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                data-testid="lobby-more-button"
                onClick={() => {
                  updateView({
                    languages,
                    statusFilter,
                    cursors: [...cursors, nextCursor],
                  })
                }}
              >
                {t('lobby.more')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
